import { ArgumentException } from '../../../exceptions/argument-exception';
import { Currency } from '../../../models/serializable/currency';
import { GenericOrder } from '../../../models/serializable/orders/generic-order';
import { GenericOrderGiftCardEntry } from '../../../models/serializable/orders/generic-order-entries/generic-order-gift-card-entry';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../../../models/serializable/orders/generic-order-entry';
import { Price } from '../../../models/serializable/price';
import { FeeHelper } from '../../fee-helper';
import { GenericOrderDiscountBreakdownEntry } from '../discount-breakdown';
import { GenericOrderFeeBreakdownEntry } from '../fee-breakdown';
import { IGenericOrderPaymentHelperAdapter } from './generic-order-payment-helper-adapter';

export class GiftCardEntryPaymentHelperAdapter extends IGenericOrderPaymentHelperAdapter {
  public readonly orderEntryTypeId: string = GenericOrderGiftCardEntry.TYPE_ID;

  static verifyType(dm: IGenericOrderPaymentHelperAdapter): boolean {
    return dm instanceof GiftCardEntryPaymentHelperAdapter;
  }

  static assertType(dm: IGenericOrderPaymentHelperAdapter): dm is GiftCardEntryPaymentHelperAdapter {
    return dm instanceof GiftCardEntryPaymentHelperAdapter;
  }

  getCurrency(order: GenericOrder, orderEntry: GenericOrderEntry): Currency | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this._getCurrency(orderEntry);
  }

  calculateDiscountBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      _orderEntryIndex: number
  ): GenericOrderDiscountBreakdownEntry[] | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateFeeBreakdown(
      order: GenericOrder,
      orderEntry: GenericOrderEntry,
      orderEntryIndex: number
  ): GenericOrderFeeBreakdownEntry[] | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    const entries: GenericOrderFeeBreakdownEntry[] = [];

    for(let ti = 0; ti < orderEntry.giftCards.length; ti++) {
      const giftCard = orderEntry.giftCards[ti];
      const result = FeeHelper.calculateFeeValueForGiftCard(giftCard);

      if(result != null) {
        entries.push({ ...result, host: { type: 'gift-card', orderEntryIndex, itemIndex: ti } });
      }
    }

    return entries;
  }

  calculateCheckoutPrice(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: true,
    });
  }

  calculateIncome(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
    });
  }

  calculateIncomeWithoutDiscount(order: GenericOrder, orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
    });
  }

  calculateDiscount(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceForDiscountProvider(orderEntry: GenericOrderEntry): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    return this.calculatePrice({
      orderEntry,
      applyFee: false,
    });
  }

  canCreateRefund(order: GenericOrder, orderEntry: GenericOrderEntry): boolean | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    if(orderEntry.status !== GenericOrderEntryStatus.Refunded) {
      return false;
    }

    return orderEntry.refunds.length <= 0;
  }

  calculateMaxSimulatedRefund(order: GenericOrder, orderEntry: GenericOrderEntry, options: { includeFee: boolean }): Price | null {
    if(!GenericOrderGiftCardEntry.assertType(orderEntry)) {
      throw new ArgumentException('order', 'Order entry type is not valid');
    }

    let orderPrice = this.calculateIncome(order, orderEntry);
    if(orderPrice == null) {
      return null;
    }

    if(orderEntry.applicationFee != null && options.includeFee) {
      orderPrice = orderPrice.add(orderEntry.applicationFee);
    }

    return orderPrice;
  }

  private calculatePrice({ orderEntry, applyFee }: {
      orderEntry: GenericOrderGiftCardEntry,
      applyFee: boolean,
    }): Price | null {
    const currency = this._getCurrency(orderEntry);
    if(currency == null) {
      return null;
    }

    let orderEntryPrice = Price.zero(currency);

    for(const giftCard of orderEntry.giftCards) {
      if(giftCard.price.currency !== currency) {
        return null;
      }

      orderEntryPrice = orderEntryPrice.add(giftCard.price);
    }

    if(applyFee) {
      if(orderEntry.applicationFee != null) {
        orderEntryPrice = orderEntryPrice.add(orderEntry.applicationFee);
      }
    }

    return orderEntryPrice;
  }

  _getCurrency(orderEntry: GenericOrderGiftCardEntry): Currency | null {
    if(orderEntry.giftCards.length <= 0) {
      return null;
    }

    return orderEntry.giftCards[0].price.currency;
  }
}
